import React, { useEffect, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

const SOURCES = ["Website", "IndiaMART", "Referral", "Exhibition", "Cold Call", "Amazon", "Walk-in", "Other"];
const STATUSES = ["New Inquiry", "Contacted", "Follow-up Required", "Interested", "Quotation Created",
  "Negotiation", "Proforma Invoice Created", "Invoice Created", "Converted", "Lost", "On Hold"];

const blank = {
  customer_name: "", company: "", phone: "", alt_phone: "", email: "", city: "", state: "",
  address: "", inquiry: "", product: "", source: "Website", status: "New Inquiry",
  assigned_to: "", notes: "", value: 0,
};

export const LeadForm = ({ open, onOpenChange, lead, onSaved }) => {
  const { user, isAdmin, can } = useAuth();
  const [form, setForm] = useState(blank);
  const [products, setProducts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dupes, setDupes] = useState([]);
  const editing = !!lead?.id;

  useEffect(() => {
    if (editing || !open) { setDupes([]); return; }
    const phone = form.phone || "";
    const email = form.email || "";
    if (phone.replace(/\D/g, "").length < 6 && !email.includes("@")) { setDupes([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/leads/duplicates", { params: { phone, email } });
        setDupes(data.matches || []);
      } catch { /* ignore */ }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [form.phone, form.email, open, editing]);

  useEffect(() => {
    if (!open) return;
    setForm(lead ? { ...blank, ...lead } : blank);
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/staff/assignable").then((r) => setStaff(r.data)).catch(() => {});
  }, [open, lead]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        const { id, lead_no, created_by, created_by_name, created_at, updated_at,
          assignment_history, is_customer, assigned_to_name, attachments, customer_no,
          customer_since, invoice_total, ...payload } = form;
        await api.patch(`/leads/${lead.id}`, payload);
        toast.success("Lead updated");
      } else {
        await api.post("/leads", { ...form, value: Number(form.value) || 0 });
        toast.success("Lead created");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err, "Could not save the lead."));
    } finally { setBusy(false); }
  };

  // anyone who can work on leads may pick the salesperson while creating one;
  // reassigning an already assigned lead stays with the admin (enforced by the API too)
  const canAssign = isAdmin || can("crm", "edit");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto scroll-slim">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{editing ? `Edit ${lead.lead_no}` : "New lead"}</DialogTitle>
          <DialogDescription>
            Capture the enquiry once. Everything else &mdash; follow-ups, quotation, invoice &mdash; flows from here.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-6">
          {dupes.length > 0 && (
            <div className="rounded-xl border border-warning/40 bg-warning-soft p-4">
              <p className="flex items-center gap-2 text-sm font-semibold text-warning">
                <AlertTriangle className="h-4 w-4" />
                This enquiry may already exist
              </p>
              <div className="mt-2 space-y-1.5">
                {dupes.map((d) => (
                  <p key={d.id} className="text-xs text-foreground">
                    <span className="num font-semibold">{d.lead_no}</span> &mdash; {d.customer_name}
                    {d.company ? ` (${d.company})` : ""} . {d.status}
                    {d.assigned_to_name ? ` . handled by ${d.assigned_to_name}` : " . unassigned"}
                  </p>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-foreground/70">
                You can still save this lead if it is genuinely a new enquiry.
              </p>
            </div>
          )}

          <section className="space-y-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Contact</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Customer name" required>
                <Input required value={form.customer_name} data-testid="lead-name"
                  onChange={(e) => set("customer_name")(e.target.value)} placeholder="Full name" />
              </Field>
              <Field label="Company name">
                <Input value={form.company} onChange={(e) => set("company")(e.target.value)} placeholder="Company" />
              </Field>
              <Field label="Contact number" required>
                <Input required value={form.phone} data-testid="lead-phone"
                  onChange={(e) => set("phone")(e.target.value)} placeholder="+91 00000 00000" />
              </Field>
              <Field label="Alternate number">
                <Input value={form.alt_phone} onChange={(e) => set("alt_phone")(e.target.value)} />
              </Field>
              <Field label="Email address">
                <Input type="email" value={form.email} onChange={(e) => set("email")(e.target.value)} />
              </Field>
              <Field label="Estimated value (INR)">
                <Input type="number" min="0" value={form.value} onChange={(e) => set("value")(e.target.value)} />
              </Field>
              <Field label="City"><Input value={form.city} onChange={(e) => set("city")(e.target.value)} /></Field>
              <Field label="State"><Input value={form.state} onChange={(e) => set("state")(e.target.value)} /></Field>
            </div>
            <Field label="Address">
              <Textarea rows={2} value={form.address} onChange={(e) => set("address")(e.target.value)} />
            </Field>
          </section>

          <section className="space-y-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Enquiry</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Interested product">
                <Select value={form.product || "none"} onValueChange={(v) => set("product")(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="lead-product"><SelectValue placeholder="Select a product" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not decided yet</SelectItem>
                    {products.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Lead source">
                <Select value={form.source} onValueChange={set("source")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Lead status">
                <Select value={form.status} onValueChange={set("status")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              {canAssign && !editing && (
                <Field label="Assign to salesperson">
                  <Select value={form.assigned_to || "none"} onValueChange={(v) => set("assigned_to")(v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="lead-assign"><SelectValue placeholder="Leave unassigned" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Leave unassigned</SelectItem>
                      {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              )}
            </div>
            <Field label="Inquiry details">
              <Textarea rows={3} value={form.inquiry} onChange={(e) => set("inquiry")(e.target.value)}
                placeholder="What exactly is the customer asking for?" />
            </Field>
            <Field label="Internal notes">
              <Textarea rows={2} value={form.notes} onChange={(e) => set("notes")(e.target.value)} />
            </Field>
          </section>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="lead-save">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {editing ? "Save changes" : "Create lead"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const Field = ({ label, required, children, hint, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs font-semibold text-foreground">
      {label} {required && <span className="text-destructive">*</span>}
    </Label>
    {children}
    {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
  </div>
);
